import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Clock, X, AlertTriangle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const RecoveryGamerPopups = ({ userId }: { userId: string }) => {
  const [activeCampaign, setActiveCampaign] = useState<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;

    const fetchCampaign = async () => {
      if (window.location.search.includes('preview_recovery=true')) {
        setActiveCampaign({
           id: 'preview-123',
           current_state: '50_PERCENT_OFFER',
           discount_percentage: 50,
           required_additional_photos: 3,
           expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString()
        });
        setIsOpen(true);
        return;
      }

      const { data } = await supabase
        .from("recovery_campaigns")
        .select("*")
        .eq("user_id", userId)
        .eq("status", "ACTIVE")
        .in("current_state", ["50_PERCENT_OFFER", "30_PERCENT_OFFER", "COUNTDOWN_2H"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data && data.expires_at) {
        if (new Date(data.expires_at) > new Date()) {
          // Check if we already showed it this session to avoid spamming
          const seenKey = `seen_recovery_popup_${data.id}`;
          if (!sessionStorage.getItem(seenKey)) {
            setActiveCampaign(data);
            setIsOpen(true);
            sessionStorage.setItem(seenKey, "true");
          }
        }
      }
    };

    fetchCampaign();
  }, [userId]);

  if (!activeCampaign) return null;

  const is50 = activeCampaign.current_state === "50_PERCENT_OFFER";

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md bg-transparent border-none shadow-none p-0 overflow-visible">
        <DialogTitle className="sr-only">Recovery Offer</DialogTitle>
        <DialogDescription className="sr-only">Special discount offer</DialogDescription>
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative bg-foreground/5 rounded-3xl overflow-hidden border border-white/10 shadow-[0_0_50px_rgba(255,106,0,0.2)]"
        >
          {/* Animated Glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200%] h-[200px] bg-gradient-to-b from-[#FF0055]/30 to-transparent blur-[50px] pointer-events-none" />

          {/* Close Button */}
          <button 
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 z-50 p-2 bg-black/50 hover:bg-white/10 rounded-full transition-colors text-white/50 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-8 flex flex-col items-center text-center relative z-10">
            <div className="w-20 h-20 bg-gradient-to-br from-[#FF0055] to-[#FF6A00] rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(255,106,0,0.5)]">
              {is50 ? (
                <Flame className="w-10 h-10 text-white" />
              ) : (
                <AlertTriangle className="w-10 h-10 text-white" />
              )}
            </div>

            <h2 className="text-3xl font-black text-white uppercase tracking-tight mb-2">
              {is50 ? "NÃO VÁ EMBORA!" : "ÚLTIMA CHANCE!"}
            </h2>
            
            <p className="text-gray-400 mb-8 max-w-sm">
              {is50 
                ? `Notamos que você deixou fotos no carrinho. Adicione mais ${activeCampaign.required_additional_photos} fotos e ganhe:` 
                : `Suas fotos estão prestes a expirar. Adicione mais ${activeCampaign.required_additional_photos} fotos e resgate agora com:`}
            </p>

            <div className="bg-black/50 border border-white/10 rounded-2xl p-6 w-full mb-8 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF0055] to-[#FF6A00] opacity-0 group-hover:opacity-10 transition-opacity" />
              <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FF0055] to-[#FF6A00] font-display">
                {activeCampaign.discount_percentage}% OFF
              </p>
              <p className="text-sm font-bold text-white/50 uppercase tracking-widest mt-2">
                Em toda a compra
              </p>
            </div>

            <Button
              onClick={() => {
                setIsOpen(false);
                navigate("/albums");
              }}
              className="w-full h-14 bg-white hover:bg-gray-200 text-black font-black uppercase tracking-widest text-sm rounded-xl flex items-center justify-center gap-3 transition-transform hover:scale-105"
            >
              RESGATAR OFERTA AGORA
              <ArrowRight className="w-5 h-5" />
            </Button>

            <p className="mt-4 flex items-center gap-2 text-xs font-bold text-[#FF6A00] uppercase tracking-widest">
              <Clock className="w-4 h-4" />
              Tempo Limitado
            </p>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};
